import { z } from 'zod';

/**
 * Cliente HTTP minimalista para la API del backend.
 *
 * - Usa `fetch` nativo (sin axios) para mantener el bundle pequeño.
 * - `VITE_API_BASE_URL` se usa como base; el dev server además proxea `/api`
 *   a `http://localhost:3000` (ver `vite.config.ts`), por lo que en dev se
 *   puede dejar vacía la base y resolverá igual.
 * - Todas las respuestas se validan con Zod antes de devolverse al consumidor.
 * - Todas las peticiones envían cookies (`credentials: 'include'`) para que el
 *   backend pueda autenticar via cookie httpOnly `tri_mentor_session`.
 */

const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  public readonly status: number;
  public readonly cause?: unknown;

  constructor(message: string, status: number, cause?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    if (cause !== undefined) {
      this.cause = cause;
    }
  }
}

/**
 * Lanzado cuando el backend devuelve 401. Consumido por la capa de auth para
 * forzar redirección al login.
 */
export class UnauthorizedError extends ApiError {
  constructor(message = 'No autenticado') {
    super(message, 401);
    this.name = 'UnauthorizedError';
  }
}

/**
 * Lanzado cuando el backend devuelve 429. Expone `retryAfterSeconds` extraído
 * del header `Retry-After` (puede ser null si el backend no lo envió).
 */
export class RateLimitedError extends ApiError {
  public readonly retryAfterSeconds: number | null;

  constructor(retryAfterSeconds: number | null, message = 'Demasiadas peticiones') {
    super(message, 429);
    this.name = 'RateLimitedError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Lanzado cuando el backend devuelve 409 (estado inconsistente: p. ej. intentar
 * setup cuando ya está inicializado, o login cuando aún no lo está).
 */
export class ConflictError extends ApiError {
  constructor(message = 'Estado del recurso en conflicto') {
    super(message, 409);
    this.name = 'ConflictError';
  }
}

function parseRetryAfter(header: string | null): number | null {
  if (header === null) return null;
  const asInt = Number.parseInt(header, 10);
  if (Number.isFinite(asInt) && asInt >= 0) return asInt;
  // Formato HTTP-date — fallback poco probable en nuestro backend.
  const asDate = Date.parse(header);
  if (Number.isFinite(asDate)) {
    return Math.max(0, Math.round((asDate - Date.now()) / 1000));
  }
  return null;
}

async function request<T>(path: string, schema: z.ZodType<T>, init?: RequestInit): Promise<T> {
  const url = `${API_BASE_URL}${path}`;
  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(init?.headers ?? {}),
      },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }

  if (response.status === 401) {
    throw new UnauthorizedError();
  }
  if (response.status === 409) {
    throw new ConflictError();
  }
  if (response.status === 429) {
    throw new RateLimitedError(parseRetryAfter(response.headers.get('Retry-After')));
  }

  if (!response.ok) {
    throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
  }

  // 204 No Content u otras respuestas vacías: validamos contra `z.void()` si aplica.
  if (response.status === 204) {
    const parsed = schema.safeParse(undefined);
    if (!parsed.success) {
      throw new ApiError('Respuesta 204 inesperada (schema esperaba contenido)', 204, parsed.error);
    }
    return parsed.data;
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch (err) {
    throw new ApiError('Respuesta del backend no es JSON válido', response.status, err);
  }

  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError('Respuesta del backend no cumple el esquema esperado', response.status, parsed.error);
  }
  return parsed.data;
}

// ---------------------------------------------------------------------------
// GET /api/health
// ---------------------------------------------------------------------------

export const HealthResponseSchema = z.object({
  status: z.literal('ok'),
  version: z.string().min(1),
  uptime: z.number().nonnegative(),
});

export type HealthResponse = z.infer<typeof HealthResponseSchema>;

export function getHealth(): Promise<HealthResponse> {
  return request('/api/health', HealthResponseSchema);
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const AuthStatusSchema = z.object({
  initialized: z.boolean(),
  authenticated: z.boolean(),
});
export type AuthStatus = z.infer<typeof AuthStatusSchema>;

export function getAuthStatus(): Promise<AuthStatus> {
  return request('/api/auth/status', AuthStatusSchema);
}

/**
 * Regex `mm:ss` — minutos sin tope realista (T-pace puede tener 1-2 dígitos
 * de min), segundos 00-59. Compartido por setup y profile update.
 */
const PaceRegex = /^[0-9]{1,2}:[0-5][0-9]$/;
const PaceString = z.string().regex(PaceRegex, 'Formato esperado mm:ss');

export const SetupRequestSchema = z.object({
  // Identidad
  name: z.string().min(1, 'El nombre es obligatorio').max(120),
  age: z.number().int().min(10).max(120).nullable().optional(),
  sex: z.enum(['M', 'F', 'X']).nullable().optional(),
  weightKg: z.number().positive().max(400).nullable().optional(),
  heightCm: z.number().positive().max(280).nullable().optional(),

  // Métricas de entrenamiento (todas opcionales)
  ftpW: z.number().int().positive().max(2000).nullable().optional(),
  lthrBpm: z.number().int().positive().max(260).nullable().optional(),
  tPaceRun: PaceString.nullable().optional(),
  tPaceSwim: PaceString.nullable().optional(),

  // Preferencias
  timezone: z.string().min(1).default('Europe/Madrid'),
  units: z.enum(['metric', 'imperial']).default('metric'),
  yearGoal: z.string().max(2000).nullable().optional(),

  // Passphrase (no se valida confirmación aquí — es responsabilidad del form)
  passphrase: z.string().min(12, 'Mínimo 12 caracteres'),
});
export type SetupRequest = z.infer<typeof SetupRequestSchema>;

const SetupResponseSchema = z.object({
  ok: z.literal(true),
  profileId: z.number().int().positive(),
});
export type SetupResponse = z.infer<typeof SetupResponseSchema>;

export function setup(body: SetupRequest): Promise<SetupResponse> {
  return request('/api/auth/setup', SetupResponseSchema, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export const LoginRequestSchema = z.object({
  passphrase: z.string().min(1),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

const LoginResponseSchema = z.object({ ok: z.literal(true) });

export function login(passphrase: string): Promise<{ ok: true }> {
  return request('/api/auth/login', LoginResponseSchema, {
    method: 'POST',
    body: JSON.stringify({ passphrase }),
  });
}

export function logout(): Promise<void> {
  return request('/api/auth/logout', z.void(), {
    method: 'POST',
  });
}

// ---------------------------------------------------------------------------
// M2.42 — Multi-user auth (signup, login email/password, password reset)
// ---------------------------------------------------------------------------
//
// Estos endpoints solo están activos cuando el backend corre en modo
// multi-usuario (`GET /api/auth/mode` → `{ multiUser: true }`). En modo
// single-user devuelven 403 y el frontend lo mapea a `MultiUserDisabledError`.

/**
 * Lanzado cuando el backend devuelve 400 en `password-reset/confirm` con un
 * token caducado, inexistente o inválido.
 */
export class InvalidTokenError extends ApiError {
  constructor(message = 'Token inválido o caducado') {
    super(message, 400);
    this.name = 'InvalidTokenError';
  }
}

/**
 * Lanzado cuando el backend devuelve 409 en `signup` porque el email ya está
 * registrado. Se separa de `ConflictError` para que la UI muestre un mensaje
 * específico ("ese email ya tiene cuenta") sin que el form tenga que distinguir
 * por endpoint.
 */
export class EmailInUseError extends ApiError {
  constructor(message = 'Email ya registrado') {
    super(message, 409);
    this.name = 'EmailInUseError';
  }
}

/**
 * Lanzado cuando el backend devuelve 401 en `login`/`signup` multi-user.
 * Alias semántico de `UnauthorizedError` para mensajes específicos del form
 * email/password sin acoplar al consumidor a la clase base.
 */
export class InvalidCredentialsError extends ApiError {
  constructor(message = 'Email o contraseña incorrectos') {
    super(message, 401);
    this.name = 'InvalidCredentialsError';
  }
}

/**
 * Lanzado cuando se intenta usar un endpoint multi-user en una instalación
 * single-user (backend responde 403). Permite a la UI ocultar formularios o
 * redirigir al flow correcto.
 */
export class MultiUserDisabledError extends ApiError {
  constructor(message = 'Modo multi-usuario no habilitado') {
    super(message, 403);
    this.name = 'MultiUserDisabledError';
  }
}

export const AuthModeResponseSchema = z.object({
  multiUser: z.boolean(),
});
export type AuthModeResponse = z.infer<typeof AuthModeResponseSchema>;

export function getAuthMode(): Promise<AuthModeResponse> {
  return request('/api/auth/mode', AuthModeResponseSchema);
}

export const SignupRequestSchema = z.object({
  email: z.string().email('Email no válido').max(254),
  password: z.string().min(8, 'Mínimo 8 caracteres').max(200),
  name: z.string().min(1, 'El nombre es obligatorio').max(120),
  age: z.number().int().min(10).max(120).nullable().optional(),
  sex: z.enum(['M', 'F', 'X']).nullable().optional(),
  weightKg: z.number().positive().max(400).nullable().optional(),
  heightCm: z.number().positive().max(280).nullable().optional(),
  timezone: z.string().min(1).default('Europe/Madrid'),
  units: z.enum(['metric', 'imperial']).default('metric'),
});
export type SignupRequest = z.infer<typeof SignupRequestSchema>;

const SignupResponseSchema = z.object({
  ok: z.literal(true),
  profileId: z.number().int().positive(),
});
export type SignupResponse = z.infer<typeof SignupResponseSchema>;

/**
 * Wrapper que llama `request` y traduce los códigos de error genéricos a las
 * clases específicas multi-user. Se hace fuera de `request` para no afectar al
 * resto de endpoints (que aún esperan `ConflictError`/`UnauthorizedError`).
 */
async function multiUserRequest<T>(
  path: string,
  schema: z.ZodType<T>,
  init: RequestInit,
  errorMap: {
    unauthorized?: () => ApiError;
    conflict?: () => ApiError;
    badRequest?: () => ApiError;
    forbidden?: () => ApiError;
  } = {},
): Promise<T> {
  try {
    return await request(path, schema, init);
  } catch (err) {
    if (err instanceof UnauthorizedError && errorMap.unauthorized) {
      throw errorMap.unauthorized();
    }
    if (err instanceof ConflictError && errorMap.conflict) {
      throw errorMap.conflict();
    }
    if (err instanceof ApiError && err.status === 400 && errorMap.badRequest) {
      throw errorMap.badRequest();
    }
    if (err instanceof ApiError && err.status === 403 && errorMap.forbidden) {
      throw errorMap.forbidden();
    }
    throw err;
  }
}

export function signup(body: SignupRequest): Promise<SignupResponse> {
  return multiUserRequest(
    '/api/auth/signup',
    SignupResponseSchema,
    { method: 'POST', body: JSON.stringify(body) },
    {
      conflict: () => new EmailInUseError(),
      forbidden: () => new MultiUserDisabledError(),
    },
  );
}

export const LoginMultiUserRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type LoginMultiUserRequest = z.infer<typeof LoginMultiUserRequestSchema>;

const LoginMultiUserResponseSchema = z.object({ ok: z.literal(true) });

export function loginMultiUser(body: LoginMultiUserRequest): Promise<{ ok: true }> {
  return multiUserRequest(
    '/api/auth/login',
    LoginMultiUserResponseSchema,
    { method: 'POST', body: JSON.stringify(body) },
    {
      unauthorized: () => new InvalidCredentialsError(),
      forbidden: () => new MultiUserDisabledError(),
    },
  );
}

export const PasswordResetRequestSchema = z.object({
  email: z.string().email(),
});
export type PasswordResetRequestBody = z.infer<typeof PasswordResetRequestSchema>;

const PasswordResetRequestResponseSchema = z.object({ ok: z.literal(true) });

export function requestPasswordReset(body: PasswordResetRequestBody): Promise<{ ok: true }> {
  return multiUserRequest(
    '/api/auth/password-reset/request',
    PasswordResetRequestResponseSchema,
    { method: 'POST', body: JSON.stringify(body) },
    {
      forbidden: () => new MultiUserDisabledError(),
    },
  );
}

export const PasswordResetConfirmSchema = z.object({
  token: z.string().min(1),
  newPassword: z.string().min(8, 'Mínimo 8 caracteres').max(200),
});
export type PasswordResetConfirmBody = z.infer<typeof PasswordResetConfirmSchema>;

const PasswordResetConfirmResponseSchema = z.object({ ok: z.literal(true) });

export function confirmPasswordReset(body: PasswordResetConfirmBody): Promise<{ ok: true }> {
  return multiUserRequest(
    '/api/auth/password-reset/confirm',
    PasswordResetConfirmResponseSchema,
    { method: 'POST', body: JSON.stringify(body) },
    {
      badRequest: () => new InvalidTokenError(),
      forbidden: () => new MultiUserDisabledError(),
    },
  );
}

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

export const ProfileSchema = z.object({
  id: z.number().int().positive(),
  name: z.string().min(1),
  age: z.number().int().nullable(),
  sex: z.enum(['M', 'F', 'X']).nullable(),
  weightKg: z.number().nullable(),
  heightCm: z.number().nullable(),
  ftpW: z.number().int().nullable(),
  lthrBpm: z.number().int().nullable(),
  tPaceRun: z.string().nullable(),
  tPaceSwim: z.string().nullable(),
  timezone: z.string(),
  units: z.enum(['metric', 'imperial']),
  yearGoal: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Profile = z.infer<typeof ProfileSchema>;

/**
 * Update parcial. Todos los campos son opcionales; los que se manden con
 * `null` borran el valor (semántica acordada con backend).
 */
export const ProfileUpdateRequestSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  age: z.number().int().min(10).max(120).nullable().optional(),
  sex: z.enum(['M', 'F', 'X']).nullable().optional(),
  weightKg: z.number().positive().max(400).nullable().optional(),
  heightCm: z.number().positive().max(280).nullable().optional(),
  ftpW: z.number().int().positive().max(2000).nullable().optional(),
  lthrBpm: z.number().int().positive().max(260).nullable().optional(),
  tPaceRun: PaceString.nullable().optional(),
  tPaceSwim: PaceString.nullable().optional(),
  timezone: z.string().min(1).optional(),
  units: z.enum(['metric', 'imperial']).optional(),
  yearGoal: z.string().max(2000).nullable().optional(),
});
export type ProfileUpdateRequest = z.infer<typeof ProfileUpdateRequestSchema>;

export function getProfile(): Promise<Profile> {
  return request('/api/profile', ProfileSchema);
}

export function updateProfile(patch: ProfileUpdateRequest): Promise<Profile> {
  return request('/api/profile', ProfileSchema, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

// ---------------------------------------------------------------------------
// GET /api/profile/suggest-metrics  (M2.14)
// ---------------------------------------------------------------------------
//
// Estimación de FTP / LTHR / T-pace a partir de las últimas N actividades.
// Cualquiera de los 4 valores puede venir `null` si no hay datos suficientes
// para esa métrica concreta.

export const SuggestMetricsResponseSchema = z.object({
  ftpW: z.number().int().positive().nullable(),
  lthrBpm: z.number().int().positive().nullable(),
  tPaceRun: z.string().regex(PaceRegex).nullable(),
  tPaceSwim: z.string().regex(PaceRegex).nullable(),
  basedOnActivities: z.number().int().nonnegative(),
  basedOnDays: z.number().int().positive(),
  methodology: z.string(),
});
export type SuggestMetricsResponse = z.infer<typeof SuggestMetricsResponseSchema>;

export function suggestMetrics(days = 30): Promise<SuggestMetricsResponse> {
  // El backend valida y rechaza `days` fuera de [7, 365]; aquí solo lo
  // serializamos. La validación dura sigue siendo del backend (single source
  // of truth) y el frontend solo se asegura de un default sensato.
  const qs = `?days=${encodeURIComponent(String(days))}`;
  return request(`/api/profile/suggest-metrics${qs}`, SuggestMetricsResponseSchema);
}
